<template>
  <div class="qls" ref="root">

    <!-- Backdrop: blocks all background clicks when any panel is open -->
<div
  v-if="open || recentOpen"
  class="qls-backdrop"
  @mousedown.stop.prevent
  @click.stop.prevent="closeAll"
  @dblclick.stop.prevent="closeAll"
  @pointerdown.stop.prevent
></div>

    <div class="qls-input-wrap" @dblclick.stop.prevent @mousedown.stop @click.stop>
      <div
        class="qls-search-side"
        :class="{ 'qls-search-side--locked': isLocked }"
        @click.stop
        @mouseup.stop
        @pointerup.stop
        @dblclick.stop.prevent
        @mousedown.stop
      >
        <i class="fa-duotone fa-magnifying-glass qls-ico"></i>

        <span class="qls-type-wrap" @dblclick.stop.prevent @mousedown.stop @click.stop>
          <select
            class="qls-type"
            v-model="searchType"
            :disabled="isLocked"
          >
            <option value="borrower">Borrower</option>
            <option value="loan">Loan #</option>
            <option value="address">Address</option>
          </select>
          <i class="fa-duotone fa-chevron-down qls-type-caret"></i>
        </span>

        <input
          class="qls-input"
          type="text"
          :placeholder="placeholder"
          v-model="query"
          :disabled="isLocked"
          @keydown="onKeyDown"
          @click.stop
          @mouseup.stop
          @dblclick.stop
        />

        <!-- Clear button -->
        <button
          v-if="query"
          class="qls-clear"
          :disabled="isLocked"
          @click.stop="clear"
        >Clear</button>

        <!-- Search button -->
        <button
          type="button"
          class="qls-search-btn"
          :disabled="isLocked || !query.trim() || query.trim().length < 2"
          @mousedown.stop
          @click.stop.prevent="fetchLoans"
          title="Search"
        >
          <i v-if="loading" class="fa-duotone fa-spinner-third qls-spin"></i>
          <i v-else class="fa-duotone fa-magnifying-glass"></i>
          <span>{{ loading ? 'Searching…' : 'Search' }}</span>
        </button>
      </div>

      <button
        type="button"
        class="qls-recent-btn"
        :disabled="isLocked"
        @mousedown.stop
        @click.stop.prevent="toggleRecentLoans"
      >
        <i class="fa-duotone fa-clock-rotate-left"></i>
        Recent Loans
      </button>
    </div>

    <!-- Search results panel -->
    <div
      v-if="open && !recentOpen"
      class="qls-panel"
      @mousedown.stop
      @click.stop
      @dblclick.stop.prevent
    >
      <div class="qls-panel-head">
        <div class="qls-panel-title">
          <i class="fa-duotone fa-magnifying-glass"></i>
          Search Results
        </div>
        <button
          type="button"
          class="qls-panel-close"
          :disabled="isLocked"
          @mousedown.stop
          @click.stop.prevent="closeAll"
        >
          <i class="fa-duotone fa-xmark"></i>
          Close
        </button>
      </div>

      <div v-if="loading" class="qls-loading">
        <i class="fa-duotone fa-spinner-third qls-spin"></i>
        Searching…
      </div>

      <div v-else-if="!items.length" class="qls-empty">
        No loans found
      </div>

      <div v-else class="qls-list">
        <div
          v-for="loan in items"
          :key="loan.id || loan.loanid || loan.f364"
          class="qls-item"
          @mousedown.stop
          @click.stop.prevent
          @dblclick.stop.prevent
        >
          <div class="qls-left">
            <div class="qls-loan-row">
              <span class="qls-loan">{{ loan.f364 || "—" }}</span>
              <span class="qls-pill">{{ loan.currentmilestone || "—" }}</span>
            </div>
            <div class="qls-name">{{ loan.borrowername || "—" }}</div>
            <div class="qls-sub">
              <i class="fa-duotone fa-location-dot qls-subico"></i>
              <span class="qls-subtext">{{ loan.f11 || "—" }}</span>
            </div>
          </div>
          <div class="qls-right">
            <div class="qls-date" v-if="loan.estimatedclosingdate">
              <i class="fa-duotone fa-calendar-days qls-dateico"></i>
              {{ formatDate(loan.estimatedclosingdate) }}
            </div>
            <button
              type="button"
              class="qls-open-btn"
              :disabled="isLocked"
              @mousedown.stop
              @click.stop.prevent="selectLoan(loan)"
            >
              Open
              <i class="fa-duotone fa-arrow-up-right-from-square"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="qls-footer">
        <span class="qls-footer-text">Press Enter or click Search — pick a type for better matches</span>
      </div>
    </div>

    <!-- Recent loans panel -->
    <div
      v-if="recentOpen"
      class="qls-panel qls-panel--recent"
      @mousedown.stop
      @click.stop
      @dblclick.stop.prevent
    >
      <div class="qls-panel-head">
        <div class="qls-panel-title">
          <i class="fa-duotone fa-clock-rotate-left"></i>
          Recently Opened Loans
        </div>
        <button
          type="button"
          class="qls-panel-close"
          :disabled="recentLoading"
          @mousedown.stop
          @click.stop.prevent="closeAll"
        >
          <i class="fa-duotone fa-xmark"></i>
          Close
        </button>
      </div>

      <div v-if="recentLoading" class="qls-loading">
        <i class="fa-duotone fa-spinner-third qls-spin"></i>
        Loading recent loans…
      </div>
      <div v-else-if="!recentItems.length" class="qls-empty">No recent loans</div>

      <div v-else class="qls-list">
        <div
          v-for="loan in recentItems"
          :key="`recent-${loan.loanId}`"
          class="qls-item"
          @mousedown.stop
          @click.stop.prevent
          @dblclick.stop.prevent
        >
          <div class="qls-left">
            <div class="qls-loan-row">
              <span class="qls-loan">{{ loan.loanNumber || "—" }}</span>
              <span class="qls-pill">{{ loan.f1172 || "—" }}</span>
            </div>
            <div class="qls-name">{{ loan.borrowerName || "—" }}</div>
            <div class="qls-sub">
              <i class="fa-duotone fa-location-dot qls-subico"></i>
              <span class="qls-subtext">{{ loan.cityState || "—" }}</span>
            </div>
            <div class="qls-chip-row">
              <span class="qls-chip" v-if="loan.loanAmount != null">
                <i class="fa-duotone fa-sack-dollar"></i>
                {{ formatMoney(loan.loanAmount) }}
              </span>
              <span class="qls-chip" v-if="loan.f19">
                <i class="fa-duotone fa-bullseye-arrow"></i>
                {{ loan.f19 }}
              </span>
            </div>
          </div>
          <div class="qls-right">
            <div class="qls-date" v-if="loan.openedAt">
              <i class="fa-duotone fa-clock"></i>
              {{ timeAgo(loan.openedAt) }}
            </div>
            <button
              type="button"
              class="qls-open-btn"
              :disabled="recentLoading"
              @mousedown.stop
              @click.stop.prevent="selectRecentLoan(loan)"
            >
              Open
              <i class="fa-duotone fa-arrow-up-right-from-square"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="qls-footer">
        <span class="qls-footer-text">Your recently opened loans</span>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"
import auth from "@/authConfig.js"

export default {
  name: "QuickLoanSearch",
  props: {
    azureId:     { type: String, required: true },
    accessToken: { type: String, required: true },
    placeholder: { type: String, default: "Search loans…" }
  },
  emits: ["select"],
  data() {
    return {
      query:         "",
      searchType:    "borrower",
      items:         [],
      recentItems:   [],
      open:          false,
      recentOpen:    false,
      loading:       false,
      recentLoading: false,
      _fetchGen:     0  // incremented each fetch; stale responses check against this
    }
  },
  computed: {
    // Single source of truth: the whole component is locked while any async op is running
    isLocked() {
      return this.loading || this.recentLoading
    }
  },
  methods: {
    // ── keyboard ──────────────────────────────────────────────────────────────
    onKeyDown(e) {
      if (e.key === "Escape") {
        this.closeAll()
      } else if (e.key === "Enter") {
        e.preventDefault()
        if (!this.isLocked && this.query.trim().length >= 2) this.fetchLoans()
      }
    },

    // ── close / clear ─────────────────────────────────────────────────────────
    closeAll() {
      this.open       = false
      this.recentOpen = false
      this.query      = ""
      this.items      = []
      // Abort any in-flight search by bumping the generation counter
      this._fetchGen++
      // If somehow still flagged loading, reset it
      this.loading       = false
      this.recentLoading = false
    },

    clear() {
      this.query = ""
      this.items = []
      this.open  = false
      this._fetchGen++
      this.loading = false
    },

    // ── filter builder ────────────────────────────────────────────────────────
    buildFilters(q) {
      const tokens = q.split(/\s+/).filter(Boolean)
      const first  = tokens[0] || ""
      const last   = tokens.length >= 2 ? tokens[tokens.length - 1] : ""

      if (this.searchType === "loan")
        return [{ field: "f364", operator: "contains", value: q }]

      if (this.searchType === "address")
        return [
          { field: "f11",   operator: "contains", value: q },
          { field: "f4000", operator: "contains", value: q },
          { field: "f4002", operator: "contains", value: q }
        ]

      if (first && last && tokens.length >= 2) {
        return [{
          logic: "or",
          filters: [
            { logic: "and", filters: [
              { field: "f4000", operator: "contains", value: first },
              { field: "f4002", operator: "contains", value: last }
            ]},
            { logic: "and", filters: [
              { field: "f4000", operator: "contains", value: last },
              { field: "f4002", operator: "contains", value: first }
            ]},
            { field: "f4000", operator: "contains", value: q },
            { field: "f4002", operator: "contains", value: q }
          ]
        }]
      }

      return [
        { field: "f4000", operator: "contains", value: q },
        { field: "f4002", operator: "contains", value: q }
      ]
    },

    // ── fetch search results ──────────────────────────────────────────────────
async fetchLoans() {
  const q = String(this.query || "").trim()
  if (!q || q.length < 2) {
    this.items = []
    this.open  = false
    return
  }

  const gen = ++this._fetchGen

  this.loading    = true
  this.recentOpen = false
  this.open       = true

  try {
    const azureId = this.azureId || localStorage.getItem("azure_id")
    const filters = this.buildFilters(q)

    const doSearch = async (token) => {
      return ApiService.GetSwiftAppPipelineV2(
        azureId,
        JSON.stringify({
          skip: 0, take: 10, sort: [], group: [],
          filter: { logic: "or", filters }
        }),
        token
      )
    }

    // First attempt with cached token
    let tokenResponse = await auth.acquireTokenResponse()
    if (gen !== this._fetchGen) return

    let token = this.accessToken || tokenResponse?.accessToken
    let res   = await doSearch(token)

    // 401 — token expired, force a fresh one and retry once
    if (res?.status === 401 || res?.response?.status === 401) {
      if (gen !== this._fetchGen) return
      tokenResponse = await auth.acquireTokenResponse({ forceRefresh: true })
      if (gen !== this._fetchGen) return
      token = tokenResponse?.accessToken
      res   = await doSearch(token)
    }

    if (gen !== this._fetchGen) return

    const rows = res?.data?.data?.contacts ?? []
    this.items = Array.isArray(rows) ? rows.slice(0, 10) : []
  } catch {
    if (gen === this._fetchGen) this.items = []
  } finally {
    if (gen === this._fetchGen) this.loading = false
  }
},

    // ── fetch recent loans ────────────────────────────────────────────────────
  async fetchRecentLoans() {
  if (!this.azureId) { this.recentItems = []; return }

  const gen = ++this._fetchGen

  this.recentLoading = true
  try {
    const doFetch = async (token) => {
      return ApiService.GetRecentLoans(this.azureId, token, 10)
    }

    // First attempt with cached token
    let tokenResponse = await auth.acquireTokenResponse()
    if (gen !== this._fetchGen) return

    let token = this.accessToken || tokenResponse?.accessToken
    let resp  = await doFetch(token)

    // 401 — token expired, force a fresh one and retry once
    if (resp?.status === 401 || resp?.response?.status === 401) {
      if (gen !== this._fetchGen) return
      tokenResponse = await auth.acquireTokenResponse({ forceRefresh: true })
      if (gen !== this._fetchGen) return
      token = tokenResponse?.accessToken
      resp  = await doFetch(token)
    }

    if (gen !== this._fetchGen) return

    const data = resp?.data?.data || resp?.data || []
    if (!Array.isArray(data)) { this.recentItems = []; return }
    const mapped = data.map(x => this.normalizeRecentLoan(x)).filter(Boolean)
    this.recentItems = this.sortRecentLoans(mapped).slice(0, 10)
  } catch {
    if (gen === this._fetchGen) this.recentItems = []
  } finally {
    if (gen === this._fetchGen) this.recentLoading = false
  }
},

    // ── normalizers / helpers ─────────────────────────────────────────────────
    normalizeRecentLoan(x) {
      if (!x) return null
      const loanId = x.loanId || x.LoanId
      if (!loanId) return null
      return {
        loanId,
        loanNumber:   x.loanNumber   || x.LoanNumber   || "",
        borrowerName: x.borrowerName || x.BorrowerName || "",
        loanAmount:   x.loanAmount   ?? x.LoanAmount   ?? null,
        cityState:    x.cityState    || x.CityState    || "",
        openedAt:     x.openedAt     || x.OpenedAt     || null,
        f1172:        x.f1172        || x.F1172        || "",
        f19:          x.f19          || x.F19          || ""
      }
    },

    sortRecentLoans(list) {
      return (Array.isArray(list) ? list : []).slice().sort((a, b) =>
        (b?.openedAt ? new Date(b.openedAt).getTime() : 0) -
        (a?.openedAt ? new Date(a.openedAt).getTime() : 0)
      )
    },

    toggleRecentLoans() {
      if (this.isLocked) return
      const next = !this.recentOpen
      this.open       = false
      this.recentOpen = next
      if (next) this.fetchRecentLoans()
    },

    selectLoan(loan) {
      if (this.isLocked) return
      this.closeAll()
      this.$emit("select", loan)
    },

    selectRecentLoan(loan) {
      if (this.isLocked) return
      this.closeAll()
      this.$emit("select", {
        id:           loan.loanId,
        loanId:       loan.loanId,
        f364:         loan.loanNumber,
        borrowername: loan.borrowerName,
        f11:          loan.cityState,
        f1172:        loan.f1172,
        f19:          loan.f19
      })
    },

    formatDate(v) {
      const d = new Date(v)
      if (isNaN(d)) return ""
      return `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}/${d.getFullYear()}`
    },

    timeAgo(value) {
      const d = new Date(value)
      if (!d || isNaN(d.getTime())) return ""
      const mins = Math.floor((Date.now() - d.getTime()) / 60000)
      if (mins < 1)  return "just now"
      if (mins < 60) return `${mins}m ago`
      const hrs = Math.floor(mins / 60)
      if (hrs < 24)  return `${hrs}h ago`
      return `${Math.floor(hrs / 24)}d ago`
    },

    formatMoney(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return "—"
      return `$${Math.round(n).toLocaleString()}`
    },

    onClickOutside(e) {
      if (!this.$refs.root) return
      if (!this.$refs.root.contains(e.target)) {
        this.closeAll()
      }
    }
  },
  mounted() {
    document.addEventListener("mousedown", this.onClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.onClickOutside)
  }
}
</script>

<style scoped>
/* ── root ──────────────────────────────────────────────────────────────────── */
.qls {
  position: relative;
  width: 100%;
}

/* ── backdrop: sits between the page and the panel, eats all pointer events ── */
.qls-backdrop {
  position: fixed;
  inset: 0;
  z-index: 4999;          /* just below the panel (5000) */
  cursor: default;
  /* Visually subtle — just enough to signal "modal-ish" without a heavy overlay */
  background: rgba(15, 23, 42, 0.18);
}

/* ── input row ─────────────────────────────────────────────────────────────── */
.qls-input-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  position: relative;
  z-index: 5001;   /* keep input row above backdrop */
  width:600px
}

.qls-search-side {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 6px 12px rgba(15, 23, 42, 0.06);
  transition: opacity 0.15s ease;
}

/* Subtle visual cue that the UI is locked */
.qls-search-side--locked {
  opacity: 0.72;
  pointer-events: none;
}

.qls-ico {
  font-size: 14px;
  opacity: 0.55;
}

.qls-type-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px 0 2px;
  margin-left: -4px;
  border-right: 1px solid rgba(15, 23, 42, 0.10);
}

.qls-type {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font-weight: 850;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.70);
  cursor: pointer;
  outline: none;
  line-height: 1;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.qls-type:disabled { cursor: not-allowed; }
.qls-type:focus    { outline: none; }

.qls-type-caret {
  font-size: 11px;
  opacity: 0.55;
  pointer-events: none;
}

.qls-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-weight: 750;
  font-size: 14px;
  color: #0f172a;
}

.qls-input:disabled { cursor: not-allowed; }

/* ── close button ──────────────────────────────────────────────────────────── */
.qls-close {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.04);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.60);
  transition: color 0.12s ease, background 0.12s ease, border-color 0.12s ease;
  flex-shrink: 0;
  white-space: nowrap;
}

.qls-close:hover {
  color: rgba(15, 23, 42, 0.90);
  background: rgba(15, 23, 42, 0.08);
  border-color: rgba(15, 23, 42, 0.20);
}

.qls-close:disabled {
  cursor: not-allowed;
  opacity: 0.30;
}

.qls-clear {
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 950;
  font-size: 12px;
  opacity: 0.65;
}

.qls-clear:hover    { opacity: 0.9; }
.qls-clear:disabled { cursor: not-allowed; opacity: 0.35; }

/* ── search button (inside the pill) ──────────────────────────────────────── */
.qls-search-btn {
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 6px 14px 6px 10px;
  font-weight: 950;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  background: rgba(37, 99, 235, 0.90);
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.22);
  transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease, opacity 0.12s ease;
  white-space: nowrap;
}

.qls-search-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.28);
  filter: brightness(1.04);
}

.qls-search-btn:active:not(:disabled) {
  transform: scale(0.97);
}

.qls-search-btn:disabled {
  cursor: not-allowed;
  opacity: 0.38;
  box-shadow: none;
}

/* ── recent button ─────────────────────────────────────────────────────────── */
.qls-recent-btn {
  flex: 0 0 auto;
  margin-left: auto;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 6px 12px rgba(15, 23, 42, 0.06);
  color: #0f172a;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 950;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease, opacity 0.15s ease;
  position: relative;
  z-index: 5001;
}

.qls-recent-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(37, 99, 235, 0.20);
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.10);
}

.qls-recent-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

/* ── panel shared ──────────────────────────────────────────────────────────── */
.qls-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 620px;
  max-width: 100%;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: #fff;
  box-shadow: 0 22px 50px rgba(15, 23, 42, 0.14);
  overflow: hidden;
  z-index: 5000;
}

.qls-panel--recent { right: 0; }

.qls-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
}

.qls-panel-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 950;
  color: #0f172a;
}

.qls-panel-close {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.04);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.60);
  transition: color 0.12s ease, background 0.12s ease, border-color 0.12s ease;
  white-space: nowrap;
}

.qls-panel-close:hover {
  color: rgba(15, 23, 42, 0.90);
  background: rgba(15, 23, 42, 0.08);
  border-color: rgba(15, 23, 42, 0.20);
}

.qls-panel-close:disabled {
  cursor: not-allowed;
  opacity: 0.30;
}

/* ── loading / empty ───────────────────────────────────────────────────────── */
.qls-loading,
.qls-empty {
  padding: 16px;
  font-weight: 900;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.60);
  display: flex;
  align-items: center;
  gap: 8px;
}

@keyframes qls-rotate {
  to { transform: rotate(360deg); }
}

.qls-spin {
  animation: qls-rotate 0.75s linear infinite;
}

/* ── list / items ──────────────────────────────────────────────────────────── */
.qls-list {
  max-height: 430px;
  overflow: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.qls-item {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px 14px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
  cursor: default;
}

.qls-item:hover {
  transform: translateY(-1px);
  border-color: rgba(37, 99, 235, 0.22);
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.10);
}

.qls-left {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.qls-loan-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.qls-loan {
  font-weight: 950;
  font-size: 13px;
  color: #0f172a;
  letter-spacing: 0.2px;
}

.qls-pill {
  font-weight: 900;
  font-size: 11px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.10);
  color: rgba(37, 99, 235, 0.95);
  border: 1px solid rgba(37, 99, 235, 0.18);
  white-space: nowrap;
}

.qls-name {
  font-weight: 850;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.82);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qls-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 750;
  font-size: 11px;
  color: rgba(15, 23, 42, 0.60);
  overflow: hidden;
}

.qls-subico  { font-size: 11px; opacity: 0.7; }
.qls-subtext { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.qls-chip-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.qls-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.03);
  font-size: 10.5px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.78);
}

.qls-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  flex: 0 0 auto;
}

.qls-date {
  font-weight: 900;
  font-size: 11px;
  color: rgba(15, 23, 42, 0.70);
  display: flex;
  align-items: center;
  gap: 6px;
}

.qls-dateico { font-size: 11px; opacity: 0.7; }

.qls-open-btn {
  border: none;
  cursor: pointer;
  border-radius: 12px;
  padding: 9px 14px;
  font-weight: 950;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  background: rgba(37, 99, 235, 0.92);
  box-shadow: 0 10px 18px rgba(37, 99, 235, 0.22);
  transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease, opacity 0.12s ease;
}

.qls-open-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.28);
  filter: brightness(1.03);
}

.qls-open-btn:active:not(:disabled) {
  transform: translateY(0px) scale(0.98);
}

.qls-open-btn:disabled {
  cursor: not-allowed;
  opacity: 0.50;
}

/* ── footer ────────────────────────────────────────────────────────────────── */
.qls-footer {
  padding: 12px 16px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
}

.qls-footer-text {
  font-weight: 800;
  font-size: 11px;
  color: rgba(15, 23, 42, 0.55);
}

/* ── responsive ────────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .qls-input-wrap {
    flex-direction: column;
    align-items: stretch;
  }
  .qls-recent-btn {
    width: 100%;
    justify-content: center;
    margin-left: 0;
  }
}
</style>